import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLoanBalanceCheck } from '@/hooks/loans/useLoans';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { AppapproDTO } from '@/shared/api/model';

interface LoanApprovalData {
  loanBalanceData: any;
  isBalanceLoading: boolean;
  balanceError: string | null;
  refetchBalance: () => void;
}

interface LoanApprovalDataProviderProps {
  children: ReactNode;
  valueData?: AppapproDTO;
  data?: any;
  formikProps?: any;
}

const LoanApprovalDataContext = createContext<LoanApprovalData | undefined>(undefined);

/**
 * LoanApprovalDataProvider - A wrapper component that provides loan balance data
 * and other approval-related data to child components through React Context.
 * 
 * This eliminates the need for each approval component to handle its own
 * loan balance fetching logic and prevents infinite re-render issues.
 */
export const LoanApprovalDataProvider: React.FC<LoanApprovalDataProviderProps> = ({
  children,
  valueData: propValueData,
  data,
  formikProps
}) => {
  const authUser = useAuthUser();
  const { branch } = authUser;
  const { checkLoanBalance } = useLoanBalanceCheck();
  
  const [loanBalanceData, setLoanBalanceData] = useState<any>(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  // Extract valueData from props or formik
  const valueData = propValueData || (formikProps?.values?.valueData) || (data?.valueData);

  // Fetch loan balance when dependencies change - using useEffect directly to avoid callback recreation
  useEffect(() => {
    const fetchLoanBalance = async () => {
      if (!valueData || !valueData.glNo || !valueData.custNo || !branch) {
        setLoanBalanceData(null);
        return;
      }

      // Only fetch for TOP_UP and REFINANCE loans
      if (valueData.disbursementType !== 'TOP_UP' && valueData.disbursementType !== 'REFINANCE') {
        setLoanBalanceData(null);
        return;
      }


      setIsBalanceLoading(true);
      setBalanceError(null);

      try {
        const balanceResult = await checkLoanBalance(branch, valueData.glNo, valueData.custNo);
        if (balanceResult) {
          setLoanBalanceData(balanceResult);
        } else {
          setLoanBalanceData(null);
        }
      } catch (error) {
        console.error('LoanApprovalDataProvider: Error fetching loan balance:', error);
        setBalanceError(error instanceof Error ? error.message : 'Failed to fetch loan balance');
        setLoanBalanceData(null);
      } finally {
        setIsBalanceLoading(false);
      }
    };

    fetchLoanBalance();
  }, [valueData?.glNo, valueData?.custNo, valueData?.disbursementType, branch]); // Removed checkLoanBalance from dependencies

  // Memoize refetch function to prevent unnecessary re-renders
  const refetchBalance = useCallback(async () => {
    if (!valueData || !valueData.glNo || !valueData.custNo || !branch) return;
    
    setIsBalanceLoading(true);
    setBalanceError(null);
    
    try {
      const balanceResult = await checkLoanBalance(branch, valueData.glNo, valueData.custNo);
      setLoanBalanceData(balanceResult || null);
    } catch (error) {
      console.error('LoanApprovalDataProvider: Error refetching loan balance:', error);
      setBalanceError(error instanceof Error ? error.message : 'Failed to fetch loan balance');
      setLoanBalanceData(null);
    } finally {
      setIsBalanceLoading(false);
    }
  }, [valueData?.glNo, valueData?.custNo, branch]); // Removed checkLoanBalance from dependencies

  const contextValue: LoanApprovalData = {
    loanBalanceData,
    isBalanceLoading,
    balanceError,
    refetchBalance
  };

  return (
    <LoanApprovalDataContext.Provider value={contextValue}>
      {children}
    </LoanApprovalDataContext.Provider>
  );
};

/**
 * Custom hook to access loan approval data from context
 */
export const useLoanApprovalData = (): LoanApprovalData => {
  const context = useContext(LoanApprovalDataContext);
  if (context === undefined) {
    throw new Error('useLoanApprovalData must be used within a LoanApprovalDataProvider');
  }
  return context;
};

export default LoanApprovalDataProvider;
